<template>
    <div>
        <div class="relative h-40"
             :class="{ hasValue: !!modelValue }">
            <div class="input-number inline-flex items-center rounded-full h-full bg-gray-400">
                <MinusCancel
                    :mode="editMode ? 'cancel' : 'minus'"
                    class="cursor-pointer minus-cancel"
                    :class="{ 'pointer-events-none': disabled, 'disabled': minusDisabled }"
                    tabindex="0"
                    @minus-click="deltaAdjustQuantity(-1)"
                    @cancel-click="done"/>
                <input
                    v-if="!disabled"
                    :id="id"
                    ref="inputElem"
                    v-model="internalValue"
                    v-autofocus="autofocus"
                    v-prohibit-zoom
                    v-whole-numbers-only
                    :name="id"
                    type="tel"
                    pattern="[0-9]{1,3}"
                    :min="minValue"
                    :max="maxValue"
                    class="input-field flex-grow flex-shrink font-semi-bold text-center min-w-0 rounded-full outline-none bg-gray-400"
                    @focus="inputFocus"
                    @blur="inputBlur"
                    @keydown.enter.esc="inputEnterEscKeyUp">
                <div
                    v-else-if="disabled"
                    class="disabled input-field w-40 border border-grey-dark p-5 flex-grow flex-shrink text-center min-w-0">
                    {{ internalValue }}
                </div>
                <PlusAccept
                    :mode="editMode ? 'accept' : 'plus'"
                    class="cursor-pointer plus-accept outline-none"
                    :class="{'pointer-events-none disabled': disabled || isAtMax || plusDisabled }"
                    tabindex="0"
                    @plus-click="deltaAdjustQuantity(1)"
                    @accept-click="setQuantity"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import PlusAccept from '@/project/basket/PlusAccept.vue';
import MinusCancel from '@/project/basket/MinusCancel.vue';

const props = defineProps({
    modelValue: {
        type: Number,
        required: true,
    },
    label: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        required: true
    },
    autofocus: {
        type: Boolean,
        default: false
    },
    minValue: {
        type: Number,
        default: 1
    },
    maxValue: {
        type: Number,
        default: Infinity
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits<{
    (e: 'input', value: number): void;
}>();

const editMode = ref(false);
const internalValue = defineModel<number>();
const inputElem = ref<HTMLInputElement>();

const id = computed(() => {
    return props.name + Math.random();
});

const isAtMax = computed(() => {
    return props.modelValue === props.maxValue;
});

const minusDisabled = computed((): boolean => {
    return props.disabled || (props.modelValue <= props.minValue && !editMode.value);
});

const plusDisabled = computed((): boolean => {
    return props.disabled || (props.modelValue >= props.maxValue && !editMode.value);
});

function inputFocus() {
    editMode.value = true;
    inputElem.value?.select();
}

function inputBlur() {
    // Need to delay until a possible click on accept/cancel arrives. This comes after blur from inputfield...
    setTimeout(() => {
        if (editMode.value) {
            // All blur events exept cancel will set quantity
            setQuantity();
            done();
        }
    }, 250);
}

function inputEnterEscKeyUp(event) {
    if (event.key === 'Enter') {
        setQuantity();
    }
    inputElem.value?.blur();
    done();
}

function done() {
    editMode.value = false;
}

function deltaAdjustQuantity(delta: number) {
    setQuantity(delta);
}

function isAllowedValue(val: number): boolean {
    return val >= props.minValue && val <= props.maxValue;
}

function setQuantity(delta: number = 0) {
    const inputValue = internalValue.value || props.modelValue || props.minValue;
    let value = parseInt(inputValue + '', 10) + delta;
    if (!isAllowedValue(value)) {
        value = value < props.minValue ? props.minValue : value > props.maxValue ? props.maxValue : props.modelValue;
    }
    internalValue.value = value;
    emit('input', value);
    done();
}

onMounted(() => {
    internalValue.value = props.modelValue;
});
</script>

<style lang="less" scoped>
.input-number {
    input {
        width: 5rem;
        @apply h-full rounded-full text-center placeholder-gray-825;
    }

    .input-field {
        max-width: 5rem;
        margin: 0;
        border-image: linear-gradient(0deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0)) 0 100%;
    }

    .plus-accept,
    .minus-cancel {
        margin: 0 0.6rem;

        &.disabled {
            cursor: default;
            opacity: 0.5;
        }
    }
}
</style>
